import React, { useState } from 'react';

import styles from './SSOLogin.module.scss';

import logo from 'assets/images/planera-logo.png';
import ErrorTooltip from 'components/ErrorTooltip/ErrorTooltip';
import { EventKey } from 'helpers/keyboardShortcuts';
import { AWS_AMPLIFY_SYSTEM_MESSAGE, ERR_EMPTY_EMAIL, ERR_INVALID_EMAIL } from 'helpers/messages';
import { isValidEmail } from 'helpers/utils';

type FormData = {
  email: string;
};

type Props = {
  onSignIn: (data: FormData) => void;
  overrideError?: string;
  onCancel: () => void;
};

function SSOLogin({ onSignIn, onCancel, overrideError }: Props) {
  const [email, setEmail] = useState('');
  const [errorToast, setErrorToast] = useState('');
  const [errorMessage, setErrorMessage] = useState(() => {
    if (overrideError) {
      return overrideError?.replace(AWS_AMPLIFY_SYSTEM_MESSAGE, '')?.trim();
    } else {
      return '';
    }
  });

  const validateEmail = (userEmail: string) => {
    setErrorToast('');
    if (!email) {
      setErrorToast(ERR_EMPTY_EMAIL);
      return false;
    } else if (!isValidEmail(userEmail)) {
      setErrorToast(ERR_INVALID_EMAIL);
      return false;
    }
    return true;
  };

  const submit = (userEmail: string) => {
    const validEmail = validateEmail(userEmail);
    if (!validEmail) return;
    // const validUser = await checkSSOUser(userEmail);
    // if (!validUser) {
    //   setErrorMessage(ERR_EMAIL_NOT_CONFIGURED_WITH_SAML);
    //   return;
    // }
    onSignIn({ email: userEmail });
  };

  return (
    <div className={styles.ssoLoginWrap}>
      <div
        data-amplify-container=""
        className={`ssoLogin ${styles.ssoLogin}`}>
        <div className="logo-wrap">
          <img
            className="logo"
            alt="Planera logo"
            src={logo}
          />
          <div className="logoTitle">Welcome to Planera</div>
        </div>
        <div
          data-amplify-router=""
          style={{ padding: '2rem' }}>
          <fieldset
            className="amplify-flex loginInputFieldWrapper"
            style={{ flexDirection: 'column' }}>
            <h3 className="amplify-heading amplify-heading--3">SAML SSO</h3>
            <div
              className="amplify-flex"
              style={{ flexDirection: 'column' }}>
              <div className="amplify-flex amplify-field amplify-textfield">
                <label
                  className="amplify-label amplify-visually-hidden"
                  htmlFor="amplify-id-6">
                  Enter your email
                </label>
                <div
                  className="amplify-flex amplify-field-group amplify-field-group--horizontal"
                  data-orientation="horizontal">
                  <div
                    className="amplify-field-group__field-wrapper amplify-field-group__field-wrapper--horizontal"
                    data-orientation="horizontal">
                    <ErrorTooltip
                      open={!!errorToast}
                      arrow
                      title={errorToast}
                      placement="right"
                      disableInteractive>
                      <input
                        autoComplete="email"
                        className={`amplify-input amplify-field-group__control ${errorToast || errorMessage ? 'error' : ''}`}
                        required
                        type="email"
                        value={email}
                        placeholder="Email"
                        name="email"
                        onKeyDown={(event) => {
                          if (event.key === EventKey.ENTER) {
                            void submit(email);
                          }
                        }}
                        onChange={(event) => {
                          setErrorMessage('');
                          setErrorToast('');
                          setEmail(event?.target?.value);
                        }}
                      />
                    </ErrorTooltip>
                    <div className={styles.errorMessage}>{errorMessage}</div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="amplify-flex"
              style={{ flexDirection: 'column' }}>
              <div className="login-terms">
                By signing in, you agree with Planera’s{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.planera.io/termsofuse"
                  className="highlighted">
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.planera.io/privacypolicy"
                  className="highlighted">
                  Privacy Policy
                </a>
                .
              </div>
              <button
                className="amplify-button amplify-field-group__control amplify-button--primary"
                data-fullwidth="false"
                data-variation="primary"
                onClick={() => submit(email)}
                style={{ fontWeight: 'var(--amplify-font-weights-normal)' }}>
                Sign In
              </button>
              <button
                className="amplify-button amplify-field-group__control amplify-button--link amplify-button--small"
                data-fullwidth="false"
                data-size="small"
                data-variation="link"
                type="button"
                onClick={onCancel}
                style={{ fontWeight: 'var(--amplify-font-weights-normal)' }}>
                Sign In with Password Instead
              </button>
            </div>
          </fieldset>
        </div>

        <div className="footer-wrap">
          <p>&copy; {new Date().getFullYear()} Planera.io All Rights Reserved</p>
        </div>
      </div>
    </div>
  );
}

export default SSOLogin;
